<template>
    <div class="profile-page bg-background">
        <!-- HERO / TITLE -->
        <v-container fluid class="hero-section py-12">

        </v-container>

        <!-- MAIN LAYOUT -->
        <v-container fluid class="py-8">
            <v-container max-width="1100" class="d-flex align-center">
                <div class="d-flex align-center ga-4">
                    <v-avatar size="56" color="primary">
                        <v-img v-if="userProfile?.profile_picture" :src="userProfile.profile_picture" cover />
                        <span v-else>
                            {{ initials }}
                        </span>
                    </v-avatar>

                    <div>
                        <h1 class="text-h5 text-md-h4 font-weight-bold mb-1">
                            Hello, {{ firstNameDisplay }}!
                        </h1>
                        <p class="text-body-2 text-medium-emphasis">
                            Manage your personal details and preferences.
                        </p>
                    </div>
                </div>
            </v-container>

            <v-container max-width="1100">
                <v-row align="start" no-gutters class="ga-md-8">
                    <!-- LEFT NAV -->
                    <v-col cols="12" md="3" class="mb-6 mb-md-0">
                        <v-sheet rounded="xl" elevation="2" class="nav-sheet">
                            <v-list nav density="comfortable">
                                <v-list-item v-for="item in navItems" :key="item.key" :value="item.key"
                                    :active="item.key === activeSection" rounded="xl" @click="activeSection = item.key">
                                    <template #prepend>
                                        <v-icon :icon="item.icon" size="20" class="mr-2" />
                                    </template>
                                    <v-list-item-title>
                                        {{ item.label }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-sheet>
                    </v-col>

                    <!-- RIGHT CONTENT -->
                    <v-col cols="12" md="8">
                        <v-sheet rounded="xl" elevation="2" class="pa-6 pa-md-8">
                            <!-- Loading state -->
                            <div v-if="isLoading" class="d-flex flex-column ga-4">
                                <v-skeleton-loader type="heading" />
                                <v-skeleton-loader v-for="n in 5" :key="n" type="text" />
                            </div>

                            <!-- Personal Information section -->
                            <template v-else-if="activeSection === 'personal'">
                                <h2 class="text-h6 text-md-h5 font-weight-bold mb-6">
                                    Personal information
                                </h2>

                                <div class="field-row" v-for="field in personalFields" :key="field.key">
                                    <div class="field-label">
                                        {{ field.label }}
                                    </div>
                                    <div class="field-value">
                                        <span v-if="field.value" class="text-body-2">
                                            {{ field.value }}
                                        </span>
                                        <span v-else class="text-body-2 text-medium-emphasis">
                                            Not provided
                                        </span>
                                    </div>
                                    <button class="field-action" type="button" @click="openEdit(field.key)">
                                        {{ field.value ? 'Edit' : 'Add' }}
                                    </button>
                                </div>
                            </template>

                            <!-- Placeholder content for other tabs -->
                            <template v-else>
                                <h2 class="text-h6 text-md-h5 font-weight-bold mb-3">
                                    {{ currentNavItem?.label }}
                                </h2>
                                <p class="text-body-2 text-medium-emphasis">
                                    This section is coming soon. For now, you can update your basic
                                    information in the Personal information tab.
                                </p>
                            </template>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { supabase } from '@/supabaseClient'

const store = useStore()
const authUser = computed(() => store.state.user)

const userProfile = ref(null)
const isLoading = ref(true)
const activeSection = ref('personal')

// left nav config
const navItems = [
    { key: 'personal', label: 'Personal Information', icon: 'mdi-account-circle-outline' },
    { key: 'security', label: 'Login & Security', icon: 'mdi-lock-outline' },
    { key: 'payments', label: 'Payment methods', icon: 'mdi-credit-card-outline' },
    { key: 'communication', label: 'Communication preferences', icon: 'mdi-email-outline' },
    { key: 'travel', label: 'Travel preferences', icon: 'mdi-airplane' },
    { key: 'site', label: 'Site preferences', icon: 'mdi-cog-outline' }
]

const currentNavItem = computed(() =>
    navItems.find(n => n.key === activeSection.value)
)

// Derived fields
const firstNameDisplay = computed(
    () => userProfile.value?.first_name || authUser.value?.first_name || 'Guest'
)

const fullName = computed(() => {
    const u = userProfile.value || {}
    const parts = [u.first_name, u.last_name].filter(Boolean)
    return parts.join(' ')
})

const initials = computed(() => {
    const u = userProfile.value || {}
    const first = (u.first_name || '').charAt(0)
    const last = (u.last_name || '').charAt(0)
    return (first + last || 'GU').toUpperCase()
})

const formattedDob = computed(() => {
    const dob = userProfile.value?.dob
    if (!dob) return ''
    const dt = new Date(dob)
    if (isNaN(dt)) return ''
    return dt.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
})

const personalFields = computed(() => {
    const u = userProfile.value || {}
    return [
        {
            key: 'full_name',
            label: 'Full name',
            value: fullName.value
        },
        {
            key: 'email',
            label: 'Email',
            value: u.email
        },
        {
            key: 'phone',
            label: 'Phone number',
            value: u.phone
        },
        {
            key: 'dob',
            label: 'Date of birth',
            value: formattedDob.value
        },
        {
            key: 'citizenship',
            label: 'Citizenship',
            value: u.citizenship
        },
        {
            key: 'language',
            label: 'Preferred language',
            value: u.language
        },
        {
            key: 'gender',
            label: 'Gender',
            value: u.gender
        }
    ]
})

// Fetch from Supabase users table
async function loadProfile() {
    try {
        isLoading.value = true
        const uid = authUser.value?.uid || authUser.value?.id
        if (!uid) {
            isLoading.value = false
            return
        }

        const { data, error } = await supabase
            .from('users')
            .select('*')
            .eq('uid', uid)
            .single()

        if (error) throw error
        userProfile.value = data
    } catch (e) {
        console.error('Error loading profile:', e)
    } finally {
        isLoading.value = false
    }
}

function openEdit(key) {
    // stub for now – here you’d open a dialog or route to an edit screen
    console.log('Edit field:', key)
}

onMounted(loadProfile)
</script>

<style scoped>
.profile-page {
    min-height: 100vh;
}

/* Left nav */
.nav-sheet {
    padding-block: 8px;
    border-radius: 24px;
    overflow: hidden;
}

/* remove list's own square background */
.nav-sheet :deep(.v-list) {
  background-color: transparent;
}

/* pill-style active item that doesn't hit the edges */
.nav-sheet :deep(.v-list-item--active) {
  background-color: rgba(0, 0, 0, 0.06);
  border-radius: 9999px;
  margin-inline: 6px;        /* keep it away from card edges */
}

/* Fields list (right side) */
.field-row {
    display: grid;
    grid-template-columns: minmax(120px, 160px) 1fr auto;
    align-items: center;
    padding-block: 14px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    gap: 8px;
}

.field-label {
    font-weight: 600;
    font-size: 0.9rem;
}

.field-value {
    font-size: 0.9rem;
}

.field-action {
    border: none;
    background: none;
    color: #000;
    text-decoration: underline;
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0;
}

/* Responsive tweaks */
@media (max-width: 959px) {
    .field-row {
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto;
    }

    .field-label {
        grid-column: 1 / 3;
    }

    .field-value {
        grid-column: 1 / 2;
    }

    .field-action {
        grid-column: 2 / 3;
        justify-self: flex-end;
    }
}
</style>
